import { useState, useEffect, useRef, lazy, Suspense } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Grid,
  InputAdornment,
  Select,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  Collapse,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { etapaLabel, canalLabel } from "./pipelineUtils";
import HistorialCompras from "./HistorialCompras";
import LeadHistorialPanel from "./LeadHistorialPanel";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import PlaceIcon from "@mui/icons-material/Place";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SaveIcon from "@mui/icons-material/Save";
import ClienteComercialResumen from "./expansion/ClienteComercialResumen";
import OportunidadFormDialog from "./expansion/OportunidadFormDialog";
import OportunidadSeguimientoDialog from "./expansion/OportunidadSeguimientoDialog";

import { draftPatch, draftChanged } from "./services/crmDraft";
const LocationPicker = lazy(() => import("./LocationPicker"));

function SectionLabel({ icon, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" mt={0.5}>
      {icon}
      <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function LeadFormDialog({ open, onClose, store, editData = null, onNuevaVenta }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const codigoPorDefecto = store.config.codigoPais || "591";
  const etapaInicial = store.config.etapasPipeline.includes("nuevo") ? "nuevo" : store.config.etapasPipeline[0];
  const codigosDisponibles = [codigoPorDefecto, ...(store.config.codigosPaisExtra || [])].filter(
    (v, i, a) => a.indexOf(v) === i
  );

  const inicial = editData || {
    nombre: "",
    edad: "",
    telefono: "",
    codigoPais: codigoPorDefecto,
    cursoInteres: "",
    montoEstimado: "",
    canal: "whatsapp",
    etapa: etapaInicial,
    observaciones: "",
    vendedorId: "",
    proximoSeguimiento: "",
  };

  const [form, setForm] = useState(inicial);
  const draftSession = useRef(null);
  const [draftError, setDraftError] = useState("");
  const liveLead = editData ? store.leads?.find(l => l.id === editData.id) : null;
  const changedRemotely = draftChanged(draftSession.current?.base, liveLead);
  const [showExtras, setShowExtras] = useState(!!editData);
  const [oppFormOpen, setOppFormOpen] = useState(false);
  const [oppEdit, setOppEdit] = useState(null);
  const [seguimientoOpp, setSeguimientoOpp] = useState(null);

  useEffect(() => {
    if (!open) { draftSession.current = null; return; }
    if (draftSession.current?.id === (editData?.id || "new")) return;
    draftSession.current = { id: editData?.id || "new", base: editData };
    setDraftError("");
    if (open) {
      setForm(editData ? {
        ...editData,
        montoEstimado: editData.montoEstimado ?? "",
      } : {
        nombre: "", edad: "", telefono: "", codigoPais: codigoPorDefecto,
        cursoInteres: "", montoEstimado: "", canal: "whatsapp", etapa: etapaInicial, observaciones: "",
        vendedorId: "", proximoSeguimiento: "", zona: "", location: null,
      });
      setShowExtras(!!editData);
    }
  }, [open, editData, codigoPorDefecto, etapaInicial]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const normalizarMontoEstimado = (valor) => {
    if (valor === "" || valor == null) return null;
    const n = parseFloat(valor);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
  };

  const handleSubmit = () => {
    if (!form.nombre.trim()) return;
    const payload = { ...form, montoEstimado: normalizarMontoEstimado(form.montoEstimado) };
    if (!editData && store.isOwner === false) payload.vendedorId = store.sellerId;

    if (editData) {
      try {
        const patch = draftPatch(draftSession.current.base, payload, liveLead);
        if (Object.keys(patch).length) store.editarLead(editData.id, patch);
      } catch (error) { setDraftError(error.message); return; }
    } else {
      store.agregarLead(payload);
    }
    setForm(inicial);
    onClose();
  };

  const handleClose = () => {
    setForm(inicial);
    onClose();
  };

  const hasZonas = store.config.zonas?.length > 0;
  const hasVendedores = store.config.vendedores.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontWeight: 700,
          bgcolor: "navy.main",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        {editData ? "Editar lead" : "Nuevo lead"}
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        {editData && <HistorialCompras store={store} leadId={editData.id} onNuevaVenta={onNuevaVenta} />}
        {editData && (
          <ClienteComercialResumen
            store={store}
            leadId={editData.id}
            onNuevaOportunidad={() => { setOppEdit(null); setOppFormOpen(true); }}
            onEditOportunidad={(o) => { setOppEdit(o); setOppFormOpen(true); }}
            onSeguimientoOportunidad={setSeguimientoOpp}
          />
        )}
        {editData && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2, border: 1, borderColor: "divider" }}>
            <LeadHistorialPanel
              leadId={editData.id}
              config={store.config}
              historialEtapas={store.historialEtapas}
              historialEdicionesLead={store.historialEdicionesLead}
              historialAsignaciones={store.historialAsignaciones}
            />
          </Box>
        )}
        <Stack spacing={2}>

          {/* ─── Datos básicos ─── */}
          <SectionLabel icon={<PersonIcon sx={{ fontSize: 16, color: "primary.main" }} />} label="Datos del lead" />

          <TextField
            label="Nombre completo"
            value={form.nombre}
            onChange={handleChange("nombre")}
            required
            fullWidth
            size="small"
            autoFocus={!isMobile}
            error={form.nombre !== undefined && form.nombre.length > 0 && !form.nombre.trim()}
          />

          <Grid container spacing={1.5}>
            <Grid item xs={4} sm={3}>
              <TextField
                label="Edad"
                value={form.edad}
                onChange={handleChange("edad")}
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={8} sm={9}>
              <TextField
                label="Teléfono"
                value={form.telefono}
                onChange={handleChange("telefono")}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Select
                        variant="standard"
                        disableUnderline
                        value={form.codigoPais || codigoPorDefecto}
                        onChange={(e) => setForm({ ...form, codigoPais: e.target.value })}
                        sx={{ fontSize: 13, minWidth: 48 }}
                      >
                        {codigosDisponibles.map((c) => (
                          <MenuItem key={c} value={c}>+{c}</MenuItem>
                        ))}
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Divider />

          {/* ─── Interés ─── */}
          <SectionLabel icon={<CategoryIcon sx={{ fontSize: 16, color: "primary.main" }} />} label="Interés y canal" />

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={7}>
              <Autocomplete freeSolo options={store.config.productos.map(p => p.nombre)}
                inputValue={form.cursoInteres || ""}
                onInputChange={(_, value) => setForm(prev => {
                  const matches = store.config.productos.filter(product => product.nombre === value);
                  return { ...prev, cursoInteres: value, productoId: matches.length === 1 ? matches[0].id : null };
                })}
                renderInput={params => <TextField {...params} label="Producto / Servicio de interés" size="small" helperText="Catálogo o texto libre" />} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Monto estimado"
                value={form.montoEstimado ?? ""}
                onChange={handleChange("montoEstimado")}
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
                size="small"
                helperText={`Oportunidad (${store.config.moneda || ""})`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <TextField
                label="Canal"
                value={form.canal}
                onChange={handleChange("canal")}
                select
                fullWidth
                size="small"
              >
                {store.config.canales.map((c) => (
                  <MenuItem key={c} value={c}>{canalLabel(c)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Etapa"
                value={form.etapa}
                onChange={handleChange("etapa")}
                select
                fullWidth
                size="small"
              >
                {[...new Set([...store.config.etapasPipeline, form.etapa])].filter(Boolean).map((e) => (
                  <MenuItem key={e} value={e}>{etapaLabel(store.config, e)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {hasZonas && (
            <TextField
              label="Zona"
              value={form.zona || ""}
              onChange={handleChange("zona")}
              select
              fullWidth
              size="small"
            >
              <MenuItem value="">Sin zona</MenuItem>
              {store.config.zonas.map((z) => (
                <MenuItem key={z} value={z}>{z}</MenuItem>
              ))}
            </TextField>
          )}

          <Divider />

          {/* ─── Ubicación ─── */}
          <SectionLabel icon={<PlaceIcon sx={{ fontSize: 16, color: "primary.main" }} />} label="Ubicación" />

          <Box sx={{ borderRadius: 2, overflow: "hidden", border: 1, borderColor: form.location ? "primary.light" : "grey.200" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" px={1.5} py={0.75} bgcolor="grey.50">
              <Typography variant="caption" color="text.secondary">
                Toca el mapa o usa GPS
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {form.location && (
                  <Chip
                    label={`${form.location.lat.toFixed(4)}, ${form.location.lng.toFixed(4)}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onDelete={() => setForm(p => ({ ...p, location: null }))}
                    sx={{ fontSize: 10, height: 22 }}
                  />
                )}
                <IconButton
                  size="small"
                  color="primary"
                  title="Usar mi ubicación"
                  onClick={() => {
                    if (!navigator.geolocation) return;
                    navigator.geolocation.getCurrentPosition(
                      (pos) => setForm(p => ({ ...p, location: { lat: pos.coords.latitude, lng: pos.coords.longitude } })),
                      () => {},
                      { enableHighAccuracy: true, timeout: 10000 }
                    );
                  }}
                >
                  <MyLocationIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
            <Suspense fallback={<Box sx={{ height: 180, bgcolor: "grey.100", display: "flex", alignItems: "center", justifyContent: "center" }}><Typography variant="caption" color="text.secondary">Cargando mapa…</Typography></Box>}>
              <LocationPicker
                value={form.location}
                onChange={(loc) => setForm(p => ({ ...p, location: loc }))}
                height={180}
                defaultCenter={store.config.ubicacionNegocio}
              />
            </Suspense>
          </Box>

          {/* ─── Extras colapsables ─── */}
          <Button
            size="small"
            onClick={() => setShowExtras(p => !p)}
            endIcon={showExtras ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            <TuneIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {showExtras ? "Menos opciones" : "Más opciones"}
          </Button>

          <Collapse in={showExtras}>
            <Stack spacing={2}>
              {hasVendedores && (
                <TextField
                  label="Vendedor asignado"
                  disabled={!store.isOwner}
                  value={form.vendedorId}
                  onChange={handleChange("vendedorId")}
                  select
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Sin asignar</MenuItem>
                  {store.config.vendedores
                    .filter((v) => v.activo || v.id === form.vendedorId)
                    .map((v) => (
                      <MenuItem key={v.id} value={v.id}>{v.nombre}</MenuItem>
                    ))}
                </TextField>
              )}
              <TextField
                label="Próximo seguimiento"
                value={form.proximoSeguimiento}
                onChange={handleChange("proximoSeguimiento")}
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
              <TextField
                label="Observaciones"
                value={form.observaciones}
                onChange={handleChange("observaciones")}
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Notas internas sobre el lead..."
              />
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>

      {draftError && <Alert severity="warning">{draftError}</Alert>}
      {!draftError && changedRemotely && <Alert severity="info">Este cliente cambió mientras lo editabas. Tu borrador se conserva; al guardar se comprobarán los campos modificados.</Alert>}
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        {!isMobile && <Button onClick={handleClose}>Cancelar</Button>}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.nombre.trim()}
          startIcon={editData ? <SaveIcon /> : <PersonAddIcon />}
          fullWidth={isMobile}
          sx={{ py: isMobile ? 1.2 : undefined, fontWeight: 700 }}
        >
          {editData ? "Guardar cambios" : "Agregar Lead"}
        </Button>
      </DialogActions>
      <OportunidadFormDialog
        open={oppFormOpen}
        onClose={() => { setOppFormOpen(false); setOppEdit(null); }}
        store={store}
        editData={oppEdit}
        defaultLeadId={editData?.id || ""}
      />
      <OportunidadSeguimientoDialog
        open={!!seguimientoOpp}
        onClose={() => setSeguimientoOpp(null)}
        store={store}
        oportunidad={seguimientoOpp}
      />
    </Dialog>
  );
}
